import { PlusOutlined } from "@ant-design/icons";
import { Col, Row, Switch } from "antd";
import cuid from "cuid";
import Processes from "entities/Processes";
import { FieldArray, Formik } from "formik";
import { find, get, isEmpty } from "lodash";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiCheck, BiChevronLeft, BiTrash } from "react-icons/bi";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import DatePicker from "shared/components/Form/DatePicker";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import RetroactivityFields from "screens/Private/Admin/InsurancePlans/RetroactivityFields";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import { INSURANCE_PLANS } from "utils/api/graphql/queries/insurance-plans";
import {
  ATTACHMENT_TYPES,
  DELTA_END_TYPES,
  DEPARTMENTS,
  GENERATED_DOCUMENTS_TYPES,
  GENERATED_DOCUMENTS_VERSIONS,
  INSURANCE_PLAN_PARAMS_RULES,
  INSURANCE_PLAN_RISKS,
  PERIOD_UNITS,
} from "utils/constants";
import { bindArrayInputProps, bindInputProps } from "utils/helpers/input";
import validation from "./validation";
import RuleMultiSelectWithExclude from "../../shared/RuleMultiSelectWithExclude";
import { applyExcludeTransformToParams } from "../../shared/subscriptionRulesExclude";

const CreateServiceView = ({ onSubmit, cancel }) => {
  const [excludeFlags, setExcludeFlags] = useState({});
  const dynamicAllValuesRef = useRef({});

  const handleSubmitWithExclude = (values) =>
    onSubmit({
      ...values,
      params: applyExcludeTransformToParams(
        values.params,
        excludeFlags,
        dynamicAllValuesRef.current,
      ),
    });
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const CREATE = t("CREATE", { returnObjects: true });
  const FORM = t("FORM", { returnObjects: true });

  const handleFileUpload = async (file, setFieldValue) => {
    const { base64, size, uid, ...fileData } = file;
    setFieldValue("iconAttachment", {
      ...fileData,
      id: cuid(),
      content: base64,
      type: ATTACHMENT_TYPES.ICON,
    });
    setFieldValue("icon", file.base64);
  };

  return (
    <Formik
      initialValues={{
        active: false,
        internal: false,
        isStandalone: true,
        name: "",
        description: "",
        alias: "",
        addons: [],
      }}
      onSubmit={handleSubmitWithExclude}
      validationSchema={validation()}
    >
      {({ isValid, handleSubmit, setFieldValue, values, ...formProps }) => [
        <Header
          key="header"
          title={CREATE.HEADER.TITLE}
          subtitle={CREATE.HEADER.ACTION}
          actions={[
            {
              text: CREATE.HEADER.DISCARD,
              onClick: cancel,
              type: "primary--outlined",
              icon: <BiChevronLeft size={20} />,
            },
            {
              text: CREATE.HEADER.SAVE,
              onClick: handleSubmit,
              type: "primary",
              icon: <BiCheck size={20} />,
            },
          ]}
        />,
        <Card key="generalInfo" divider={false} title={FORM.GENERAL_INFO}>
          <div className="grid--1">
            <div className="grid--3">
              <div className="d-flex">
                <span>{FORM.ACTIVE}</span>
                <Switch
                  className="button__switch"
                  checkedChildren={FORM.YES}
                  unCheckedChildren={FORM.NO}
                  defaultChecked={false}
                  onChange={(e) => setFieldValue("active", e)}
                />
              </div>
              <div className="d-flex">
                <span>{FORM.INTERNAL}</span>
                <Switch
                  className="button__switch"
                  checkedChildren={FORM.YES}
                  unCheckedChildren={FORM.NO}
                  defaultChecked={false}
                  onChange={(e) => setFieldValue("internal", e)}
                />
              </div>
              <div className="d-flex">
                <span>{FORM.STANDALONE}</span>
                <Switch
                  className="button__switch"
                  checkedChildren={FORM.YES}
                  unCheckedChildren={FORM.NO}
                  defaultChecked={true}
                  onChange={(e) => setFieldValue("isStandalone", e)}
                />
              </div>
            </div>
            <div className="grid--2">
              <Input
                placeholder={FORM.NAME}
                label={FORM.NAME}
                {...bindInputProps({ name: "name", values, ...formProps })}
              />
              <Input
                placeholder={FORM.DESCRIPTION}
                label={FORM.DESCRIPTION}
                {...bindInputProps({
                  name: "description",
                  values,
                  ...formProps,
                })}
              />
              <Input
                placeholder={FORM.ALIAS}
                label={FORM.ALIAS}
                {...bindInputProps({ name: "alias", values, ...formProps })}
              />
              <DatePicker
                placeholder={FORM.DATE}
                label={FORM.DATE}
                placeholders={[FORM.START_DATE, FORM.END_DATE]}
                onChange={(_, dates) => setFieldValue("date", dates)}
              />
              <Input
                type="file"
                variant="uploader"
                listType="picture"
                showUploadList={false}
                maxCount={1}
                onUpload={([file]) => handleFileUpload(file, setFieldValue)}
                fileList={
                  isEmpty(get(values, "icon", [])) ? [] : [get(values, "icon")]
                }
              >
                <Button type="primary--link">{FORM.ICON}</Button>
              </Input>
            </div>
          </div>
        </Card>,
        <div key="margin-3" className="m-bottom-16" />,
        <Card divider={false} title={FORM.CONDITIONS.TITLE} key="rules">
          <FieldArray
            name={"params"}
            render={(arrayHelpers) => [
              get(values, "params", []).map((_, i) => [
                <Col span={24} key="conditions">
                  <div key="params" className="grid--1">
                    <div
                      className={"d-flex justify--between m-top-8 delete-icon"}
                    >
                      <h3 className="card--title">{`${
                        FORM.CONDITIONS.PARAMETERS
                      } ${i + 1}`}</h3>
                      <BiTrash
                        color={COLORS.C_DANGER}
                        size={24}
                        onClick={() => {
                          arrayHelpers.remove(i);
                        }}
                      />
                    </div>
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={4}>{FORM.CONDITIONS.ISSUANCE_DATE}</Col>
                      <Col sm={24} md={10}>
                        <Row align={"middle"} gutter={8}>
                          <Col>{FORM.CONDITIONS.TODAY + " +"}</Col>
                          <Col flex="1">
                            <Input
                              type="number"
                              placeholder={FORM.CONDITIONS.START_DATE}
                              {...bindArrayInputProps({
                                parent: "params",
                                index: i,
                                name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_START}.value`,
                                values,
                                ...formProps,
                              })}
                            />
                          </Col>
                        </Row>
                      </Col>
                      <Col sm={24} md={10}>
                        <Row align={"middle"} gutter={8}>
                          <Col flex={1}>
                            <Select
                              options={Object.entries(DELTA_END_TYPES).map(
                                ([key, value]) => ({
                                  label:
                                    FORM.CONDITIONS.MAX_ISSUANCE_DATE_OPTIONS[
                                      key
                                    ],
                                  value,
                                }),
                              )}
                              allowClear
                              placeholder={FORM.CONDITIONS.END_DATE}
                              {...bindArrayInputProps({
                                parent: "params",
                                index: i,
                                name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END}.type`,
                                values,
                                ...formProps,
                              })}
                              onChange={(value) => {
                                value
                                  ? setFieldValue(
                                      `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END}.type`,
                                      value,
                                    )
                                  : setFieldValue(
                                      `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END}`,
                                      null,
                                    );
                              }}
                            />
                          </Col>
                          {get(
                            values,
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END}.type`,
                          ) === DELTA_END_TYPES.ADD_PERIOD && (
                            <>
                              <Col span={9}>
                                <Input
                                  type="number"
                                  placeholder={FORM.CONDITIONS.NUMBER_OF}
                                  {...bindArrayInputProps({
                                    parent: "params",
                                    index: i,
                                    name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END}.value`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                              </Col>
                              <Col span={5}>
                                <Select
                                  options={Object.entries(PERIOD_UNITS).map(
                                    ([key, value]) => ({
                                      label: FORM.CONDITIONS[key],
                                      value,
                                    }),
                                  )}
                                  placeholder={FORM.CONDITIONS.UNIT}
                                  {...bindArrayInputProps({
                                    parent: "params",
                                    index: i,
                                    name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END}.unit`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                              </Col>
                            </>
                          )}
                        </Row>
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={4}>{FORM.CONDITIONS.ISSUANCE_DATE_RIA}</Col>
                      <Col span={10}>
                        <Row align={"middle"} gutter={8}>
                          <Col>{FORM.CONDITIONS.TODAY + " +"}</Col>
                          <Col flex="1">
                            <Input
                              type="number"
                              placeholder={FORM.CONDITIONS.START_DATE_RIA}
                              {...bindArrayInputProps({
                                parent: "params",
                                index: i,
                                name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_START_RIA}.value`,
                                values,
                                ...formProps,
                              })}
                            />
                          </Col>
                        </Row>
                      </Col>
                      <Col span={10}>
                        <Row align={"middle"} gutter={8}>
                          <Col flex={1}>
                            <Select
                              options={Object.entries(DELTA_END_TYPES).map(
                                ([key, value]) => ({
                                  label:
                                    FORM.CONDITIONS.MAX_ISSUANCE_DATE_OPTIONS[
                                      key
                                    ],
                                  value,
                                }),
                              )}
                              placeholder={FORM.CONDITIONS.END_DATE_RIA}
                              {...bindArrayInputProps({
                                parent: "params",
                                index: i,
                                name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA}.type`,
                                values,
                                ...formProps,
                              })}
                              allowClear
                              onChange={(value) => {
                                value
                                  ? setFieldValue(
                                      `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA}.type`,
                                      value,
                                    )
                                  : setFieldValue(
                                      `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA}`,
                                      null,
                                    );
                              }}
                            />
                          </Col>
                          {get(
                            values,
                            `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA}.type`,
                          ) === DELTA_END_TYPES.ADD_PERIOD && (
                            <>
                              <Col span={9}>
                                <Input
                                  type="number"
                                  placeholder={FORM.CONDITIONS.NUMBER_OF}
                                  {...bindArrayInputProps({
                                    parent: "params",
                                    index: i,
                                    name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA}.value`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                              </Col>
                              <Col span={5}>
                                <Select
                                  options={Object.entries(PERIOD_UNITS).map(
                                    ([key, value]) => ({
                                      label: FORM.CONDITIONS[key],
                                      value,
                                    }),
                                  )}
                                  placeholder={FORM.CONDITIONS.UNIT}
                                  {...bindArrayInputProps({
                                    parent: "params",
                                    index: i,
                                    name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA}.unit`,
                                    values,
                                    ...formProps,
                                  })}
                                />
                              </Col>
                            </>
                          )}
                        </Row>
                      </Col>
                    </Row>
                    <RetroactivityFields
                      index={i}
                      values={values}
                      setFieldValue={setFieldValue}
                      formProps={formProps}
                      label={FORM.CONDITIONS.RETROACTIVITY_ENABLED}
                      daysLabel={FORM.CONDITIONS.RETROACTIVITY_DAYS}
                      daysPlaceholder={FORM.CONDITIONS.DAYS}
                    />
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={4}>{FORM.CONDITIONS.AGE}</Col>
                      <Col span={10}>
                        <Input
                          type="number"
                          placeholder={FORM.CONDITIONS.MIN}
                          {...bindArrayInputProps({
                            parent: "params",
                            index: i,
                            name: `rules.${INSURANCE_PLAN_PARAMS_RULES.MIN_AGE}.value`,
                            values,
                            ...formProps,
                          })}
                        />
                      </Col>
                      <Col sm={10}>
                        <Input
                          type="number"
                          placeholder={FORM.CONDITIONS.MAX}
                          {...bindArrayInputProps({
                            parent: "params",
                            index: i,
                            name: `rules.${INSURANCE_PLAN_PARAMS_RULES.MAX_AGE}.value`,
                            values,
                            ...formProps,
                          })}
                        />
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={4}>{FORM.CONDITIONS.DEPARTMENTS}</Col>
                      <Col sm={20} lg={20}>
                        <RuleMultiSelectWithExclude
                          paramIndex={i}
                          ruleName={INSURANCE_PLAN_PARAMS_RULES.DEPARTMENTS}
                          excludeLabel={FORM.CONDITIONS.EXCLUDED}
                          excludeFlags={excludeFlags}
                          setExcludeFlags={setExcludeFlags}
                          allOptionValues={DEPARTMENTS}
                          allOptionValuesRef={dynamicAllValuesRef}
                          mode="multiple"
                          options={DEPARTMENTS.map((value) => ({
                            value,
                            label: value,
                          }))}
                          placeholder={FORM.CONDITIONS.DEPARTMENTS}
                          {...bindArrayInputProps({
                            parent: "params",
                            index: i,
                            name: `rules.${INSURANCE_PLAN_PARAMS_RULES.DEPARTMENTS}.value`,
                            values,
                            ...formProps,
                          })}
                          value={
                            get(
                              values,
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DEPARTMENTS}.value`,
                            ) || []
                          }
                          onChange={(value) =>
                            setFieldValue(
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DEPARTMENTS}.value`,
                              value,
                            )
                          }
                        />
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={4}>{FORM.CONDITIONS.ZIP_CODES}</Col>
                      <Col sm={20} lg={20}>
                        <Select
                          mode="tags"
                          maxTagCount="responsive"
                          placeholder={FORM.CONDITIONS.ZIP_CODES}
                          {...bindArrayInputProps({
                            parent: "params",
                            index: i,
                            name: `rules.${INSURANCE_PLAN_PARAMS_RULES.ZIP_CODES}.value`,
                            values,
                            ...formProps,
                          })}
                          value={
                            get(
                              values,
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.ZIP_CODES}.value`,
                            ) || []
                          }
                          onChange={(value) =>
                            setFieldValue(
                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.ZIP_CODES}.value`,
                              value,
                            )
                          }
                        />
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]} align={"middle"}>
                      <Col span={4}>{FORM.CONDITIONS.PROCESSES}</Col>
                      <Col span={20}>
                        <Processes
                          NoData={() => <></>}
                          View={({ data }) => (
                            <Row gutter={[16, 16]} align={"middle"}>
                              <Col span={24}>
                                <RuleMultiSelectWithExclude
                                  paramIndex={i}
                                  ruleName={
                                    INSURANCE_PLAN_PARAMS_RULES.PROCESSES
                                  }
                                  excludeLabel={FORM.CONDITIONS.EXCLUDED}
                                  excludeFlags={excludeFlags}
                                  setExcludeFlags={setExcludeFlags}
                                  allOptionValues={data?.map(({ id }) => id)}
                                  allOptionValuesRef={dynamicAllValuesRef}
                                  mode="multiple"
                                  options={data?.map(({ id, name }) => ({
                                    value: id,
                                    label: name,
                                  }))}
                                  placeholder={FORM.CONDITIONS.PROCESSES}
                                  {...bindArrayInputProps({
                                    parent: "params",
                                    index: i,
                                    name: `rules.${INSURANCE_PLAN_PARAMS_RULES.PROCESSES}.value`,
                                    values,
                                    ...formProps,
                                  })}
                                  value={
                                    get(
                                      values,
                                      `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.PROCESSES}.value`,
                                    ) || []
                                  }
                                  onChange={(value) =>
                                    setFieldValue(
                                      `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.PROCESSES}.value`,
                                      value,
                                    )
                                  }
                                />
                              </Col>
                              <Col span={24}>
                                <Row gutter={[16, 16]} align={"middle"}>
                                  {get(
                                    values,
                                    `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.PROCESSES}.value`,
                                    [],
                                  ).map((process, j) => (
                                    <>
                                      <Col span={24}>
                                        <h3>
                                          {get(
                                            find(
                                              data,
                                              ({ id }) => id === process,
                                            ),
                                            "name",
                                          )}
                                        </h3>
                                      </Col>
                                      <Col span={24}>
                                        <Row gutter={[16, 16]}>
                                          <FieldArray
                                            key={`process-params${process}`}
                                            name={`params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS}.value.${j}.documents`}
                                            render={(arrayHelpers) => [
                                              get(
                                                values,
                                                `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS}.value.${j}.documents`,
                                                [],
                                              ).map((_, index) => (
                                                <>
                                                  <Col span={7}></Col>
                                                  <Col span={8}>
                                                    <Select
                                                      options={GENERATED_DOCUMENTS_TYPES.map(
                                                        (key) => ({
                                                          label:
                                                            CREATE.DOCUMENTS[
                                                              key
                                                            ].TITLE,
                                                          value: key,
                                                        }),
                                                      )}
                                                      placeholder={
                                                        CREATE.DOCUMENTS
                                                          .PLACEHOLDER
                                                      }
                                                      {...bindArrayInputProps({
                                                        parent: `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS}.value.${j}.documents`,
                                                        index,
                                                        name: `documentName`,
                                                        values,
                                                        ...formProps,
                                                      })}
                                                    />
                                                  </Col>
                                                  <Col span={8}>
                                                    <Select
                                                      options={GENERATED_DOCUMENTS_VERSIONS[
                                                        get(
                                                          values,
                                                          `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS}.value.${j}.documents.${index}.documentName`,
                                                        )
                                                      ].map((key) => ({
                                                        label:
                                                          CREATE.DOCUMENTS[
                                                            get(
                                                              values,
                                                              `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS}.value.${j}.documents.${index}.documentName`,
                                                            )
                                                          ]?.TEMPLATES[key],
                                                        value: key,
                                                      }))}
                                                      placeholder={
                                                        CREATE.DOCUMENTS
                                                          .PLACEHOLDER
                                                      }
                                                      {...bindArrayInputProps({
                                                        parent: `params.${i}.rules.${INSURANCE_PLAN_PARAMS_RULES.DOCUMENTS}.value.${j}.documents`,
                                                        index,
                                                        name: `documentVersion`,
                                                        values,
                                                        ...formProps,
                                                      })}
                                                    />
                                                  </Col>
                                                  <Col span={1}>
                                                    <BiTrash
                                                      color={COLORS.C_DANGER}
                                                      size={24}
                                                      onClick={() => {
                                                        arrayHelpers.remove(i);
                                                      }}
                                                    />
                                                  </Col>
                                                </>
                                              )),
                                              <Col span={24}>
                                                <Button
                                                  type="primary"
                                                  key="add-row"
                                                  onClick={() => {
                                                    arrayHelpers.push({
                                                      process,
                                                    });
                                                  }}
                                                >
                                                  <PlusOutlined
                                                    color={COLORS.C_WHITE}
                                                  />
                                                </Button>
                                              </Col>,
                                            ]}
                                          />
                                        </Row>
                                      </Col>
                                    </>
                                  ))}
                                </Row>
                              </Col>
                            </Row>
                          )}
                        />
                      </Col>
                    </Row>
                  </div>
                </Col>,
              ]),
              <div className="m-top-16" key="divider" />,
              <Button
                type="primary"
                key="add-row"
                onClick={() => {
                  arrayHelpers.push({
                    id: cuid(),
                    rules: {},
                  });
                }}
              >
                <PlusOutlined color={COLORS.C_WHITE} />
              </Button>,
            ]}
          />
        </Card>,
        <div key="margin-4" className="m-bottom-16" />,
        get(values, "isStandalone") && (
          <Card key="addons" divider={false} title={FORM.SERVICE_ADDONS}>
            <div className="grid--1">
              <Select
                type="async"
                mode="multiple"
                allowClear
                node={{
                  query: INSURANCE_PLANS,
                  variables: {
                    where: {
                      risk: INSURANCE_PLAN_RISKS.SERVICE,
                      isStandalone: false,
                    },
                    take: null,
                  },
                  accessors: {
                    root: "insurancePlans.data",
                    label: "name",
                  },
                }}
                label={FORM.SELECT_ADDONS}
                placeholder={FORM.SELECT_ADDONS}
                {...bindInputProps({ name: "addons", values, ...formProps })}
                onChange={(selected) => setFieldValue("addons", selected)}
              />
            </div>
          </Card>
        ),
      ]}
    </Formik>
  );
};

export default CreateServiceView;
